import React, { FunctionComponent, useEffect, useRef } from 'react';

import { m as motion, useAnimation } from 'framer-motion';
import { AnimationControls } from 'framer-motion/types/animation/types';
import styled from 'styled-components';

import { Button } from 'chime-ui/components/Button';
import { iconsPath, MoneyBox } from 'chime-ui/components/Icons';
import {
  StrongText,
  Superscript,
  Text,
  Title,
} from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';
import media from 'chime-ui/system/media';

import Layout, { CopyrightText, Footer } from 'components/Layout';
import Header from 'components/Layout/Header';
import {
  contentAnimations,
  iconAnimations,
  titleAnimations,
} from 'components/SubPages/Confirmation/Confirmation.animations';

import { useBonusAmount } from 'hooks/useBonusAmount';
import { useIntersect } from 'hooks/useIntersect';
import useP2PEnrollmentFlow from 'hooks/useP2PEnrollmentFlow';
import { useUserContext } from 'hooks/useUserContext';
import { useValuePropsSegmentEvents } from 'hooks/useValuePropsSegmentEvents';

import ConfirmationWithoutBonus from '../features/successPageMessaging/ConfirmationWithoutBonus';
import ConfirmationTermsAndConditionsValueProps from './ConfirmationTermsAndConditionsValueProps';
import usePayAnyoneConfirmationPage from './usePayAnyoneConfirmationPage';

const PayAnyoneConfirmation: FunctionComponent = () => {
  const containerRef = useRef(null);
  const [userData] = useUserContext();
  const { bonusAmount } = useBonusAmount();
  const {
    claimWithChime: isClaimWithChimeEnabled,
    amount,
    bank,
    name,
  } = userData;
  const { trackCtaButtonClick } = usePayAnyoneConfirmationPage();

  const { activateP2pEnrollmentAndNavigate } = useP2PEnrollmentFlow(
    isClaimWithChimeEnabled,
    null
  );

  const animationControls = useAnimation();
  useEffect(() => {
    const startAnimation = async () => {
      await animationControls.start('showTitle');
      await animationControls.start('showIcon');
      await animationControls.start('contentSlide');
    };

    startAnimation();
  }, [animationControls]);

  const [setNode, entry] = useIntersect({
    root: null,
    rootMargin: '0px',
    threshold: [0.5, 0.9],
  });

  useEffect(() => {
    setNode(containerRef.current);
  }, [setNode]);

  useValuePropsSegmentEvents(entry, 'PayAnyone Confirmation Page');

  // TODO: Remove conditional render once migration is complete: https://chime.atlassian.net/browse/PFG-854
  if (!isClaimWithChimeEnabled) return <ConfirmationWithoutBonus />;

  return (
    <Layout
      header={<Header />}
      footer={
        <AnimatedFooter bank={bank} animationControls={animationControls} />
      }
    >
      <MoneyIconLayout
        initial={'hidden'}
        animate={animationControls}
        variants={iconAnimations}
      >
        <MoneyBox />
      </MoneyIconLayout>
      <TitleLayout
        initial={'hidden'}
        animate={animationControls}
        variants={titleAnimations}
      >
        <Title size={'small'}>
          <Text color={colors.darkGreen}>${amount}</Text> is coming!
          <br />
          Want another ${bonusAmount}?
        </Title>
      </TitleLayout>
      <motion.div
        initial={'hidden'}
        animate={animationControls}
        variants={contentAnimations}
      >
        <Separator />
        <ContentLayout ref={containerRef}>
          <ValuePropsLayout>
            <ValuePropsTitle>
              <StrongText>
                Join {name} on Chime to get{' '}
                <Text color={colors.darkGreen}>another ${bonusAmount}</Text>
                <Superscript>*</Superscript>
              </StrongText>
            </ValuePropsTitle>
            <ListBox>
              <img
                src={`${iconsPath}payRequestIconSmall.svg`}
                alt={`Paid up to 2 days early with direct deposit`}
              />
              <Text>
                Pay Anyone fee-free, no matter what bank account they use
              </Text>
            </ListBox>
            <ListBox>
              <img
                src={`${iconsPath}cashOut.svg`}
                alt={`Fee-free overdraft up to $200`}
              />
              <Text>
                Cash-out instantly<Superscript>1</Superscript> with no fees
              </Text>
            </ListBox>
            <ListBox>
              <img
                src={`${iconsPath}fedBuilding.svg`}
                alt={`An extra $${bonusAmount} for joining`}
              />
              <Text>
                Your funds are FDIC insured up to $250,000 through our bank
                partners<Superscript>2</Superscript>
              </Text>
            </ListBox>
          </ValuePropsLayout>
        </ContentLayout>

        <ButtonLayout>
          <Button
            onClick={() =>
              activateP2pEnrollmentAndNavigate(trackCtaButtonClick)
            }
          >
            Join Chime to get another ${bonusAmount}
          </Button>
        </ButtonLayout>
      </motion.div>
    </Layout>
  );
};

function AnimatedFooter({
  bank,
  animationControls,
}: {
  bank: string;
  animationControls: AnimationControls;
}) {
  return (
    <motion.div
      initial={'hidden'}
      animate={animationControls}
      variants={contentAnimations}
    >
      <ConfirmationTermsAndConditionsValueProps bank={bank} />
      <Footer>{<CopyrightText />}</Footer>
    </motion.div>
  );
}

const MoneyIconLayout = styled(motion.div)`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ButtonLayout = styled.div`
  ${media.tablet`
    margin-bottom: 2rem;
  `};
`;

const ContentLayout = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

const TitleLayout = styled(motion.div)`
  margin: 2rem 0;
  text-align: center;
`;

const Separator = styled.div`
  height: 0;
  width: 112px;
  border-top: solid 1px ${colors.lightGrey};
  margin: 0 auto;
`;

const ValuePropsLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 1.5rem;
`;

const ListBox = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const ValuePropsTitle = styled.div`
  text-align: center;
`;

export default PayAnyoneConfirmation;
